import {
  getCurrencyDrawerOptions,
  getCustomerDrawerOptions,
  getTimeDrawerOptions,
  getWorkflowDrawerOptions,
  prettyName,
  ProductTypeEnum,
  productTypeLabel,
  RFQProtocolEnum,
  transformDrawerValueToNumber,
  type ColumnDef,
  type Currency,
  type Customer,
  type ICustomerOrderExecutionRule,
  type InputsAndDropdownsDrawerOption,
  type Security,
} from '@talos/kyoko';
import { DEFAULT_CUSTOMER_CREDITS_CURRENCIES } from '../CustomerCredits/utils';
import { isActiveRuleColumn } from '../columns';
import { hasNotionalThreshold, validatePriority, validateTimeWindow } from './validation';

function getNotionalCurrencyOptions({
  creditCurrencies,
  securitiesBySymbol,
  symbol,
}: {
  creditCurrencies: Pick<Currency, 'Symbol' | 'Description'>[];
  securitiesBySymbol: Map<string, Security>;
  symbol: string | undefined;
}): Pick<Currency, 'Symbol' | 'Description'>[] {
  const optionsBySymbol = new Map(creditCurrencies.map(currency => [currency.Symbol, currency]));
  const quoteCurrency = symbol ? securitiesBySymbol.get(symbol)?.QuoteCurrency : undefined;
  if (quoteCurrency && !optionsBySymbol.has(quoteCurrency)) {
    optionsBySymbol.set(quoteCurrency, { Symbol: quoteCurrency, Description: '' });
  }
  return Array.from(optionsBySymbol.values());
}

export function getRFQProtocolRulesDrawerOptions({
  customers,
  securitiesBySymbol,
  creditCurrencies = DEFAULT_CUSTOMER_CREDITS_CURRENCIES,
  allowSyntheticRFQ,
}: {
  customers: Customer[];
  securitiesBySymbol: Map<string, Security>;
  creditCurrencies?: Pick<Currency, 'Symbol' | 'Description'>[];
  allowSyntheticRFQ?: boolean;
}): InputsAndDropdownsDrawerOption<ICustomerOrderExecutionRule>[] {
  return [
    {
      field: 'RFQProtocol',
      type: 'dropdown',
      title: 'RFQ Protocol',
      guideText: 'Pricing protocol applied when this rule matches: Systematic (automatic) or Manual.',
      getIsRequired: () => true,
      getDropdownOptions: () => [
        {
          value: RFQProtocolEnum.Systematic,
          label: prettyName(RFQProtocolEnum.Systematic),
          description: 'Automatic quote pricing for Customer RFQ',
        },
        {
          value: RFQProtocolEnum.Manual,
          label: prettyName(RFQProtocolEnum.Manual),
          description: 'Manual quote pricing for Customer RFQ',
        },
        ...(allowSyntheticRFQ
          ? [
              {
                value: RFQProtocolEnum.Synthetic,
                label: prettyName(RFQProtocolEnum.Synthetic),
                description: 'Synthetic quote pricing for Customer RFQ',
              },
            ]
          : []),
      ],
    },
    {
      field: 'Priority',
      type: 'inputNumeric',
      title: 'Priority',
      guideText:
        "Rule evaluation priority. A lower value means higher priority. Ties are broken by the rule's creation timestamp.",
      getIsRequired: () => true,
      numberInputProps: { integerOnly: true, min: '0' },
      transformControlToDrawerRecord: transformDrawerValueToNumber,
      validate: form => validatePriority(form),
    },
    { type: 'divider' },
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Counterparty',
      placeholder: '*',
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      title: 'Symbol',
      placeholder: '*',
      onChange: (newSymbol, onFormUpdate) => {
        if (!newSymbol) {
          // Use '' so EntityAdmin PATCH overwrites the cloned row (undefined is skipped).
          onFormUpdate('MinQuantityThreshold', '');
          onFormUpdate('MaxQuantityThreshold', '');
          return;
        }
        const quoteCurrency = securitiesBySymbol.get(newSymbol)?.QuoteCurrency;
        if (quoteCurrency) {
          onFormUpdate('NotionalCurrency', quoteCurrency);
        }
      },
    },
    {
      field: 'ProductType',
      type: 'dropdown',
      title: 'Product Type',
      placeholder: '*',
      getDropdownOptions: () =>
        Object.values(ProductTypeEnum).map(productType => ({
          value: productType,
          label: productTypeLabel(productType),
        })),
    },
    {
      field: 'Workflow',
      type: 'dropdown',
      title: 'Workflow',
      placeholder: '*',
      guideText: 'Leave blank to match all workflows.',
      getDropdownOptions: () => getWorkflowDrawerOptions(),
    },
    {
      field: 'StartTime',
      type: 'dropdown',
      title: 'Start Time',
      guideText: 'Time of day (UTC) when this rule becomes active, in HH:MM:SS format.',
      getDropdownOptions: () => getTimeDrawerOptions(),
      validate: form => validateTimeWindow(form),
    },
    {
      field: 'EndTime',
      type: 'dropdown',
      title: 'End Time',
      guideText: 'Time of day (UTC) when this rule deactivates, in HH:MM:SS format.',
      getDropdownOptions: () => getTimeDrawerOptions(),
      validate: form => validateTimeWindow(form),
    },
    {
      field: 'MinQuantityThreshold',
      type: 'input',
      title: 'Min Quantity Threshold',
      guideText: 'Minimum order size (in base currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.BaseCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
    {
      field: 'MaxQuantityThreshold',
      type: 'input',
      title: 'Max Quantity Threshold',
      guideText: 'Maximum order size (in base currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.BaseCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
    {
      field: 'NotionalCurrency',
      type: 'dropdown',
      title: 'Notional Currency',
      guideText: 'Currency used for notional thresholds. Defaults to the selected symbol quote currency.',
      getIsRequired: form => hasNotionalThreshold(form),
      getDropdownOptions: form =>
        getCurrencyDrawerOptions(
          getNotionalCurrencyOptions({
            creditCurrencies,
            securitiesBySymbol,
            symbol: form.Symbol,
          })
        ),
    },
    {
      field: 'MinNotionalThreshold',
      type: 'input',
      title: 'Min Notional Threshold',
      guideText: 'Minimum order notional value for this rule to apply.',
      getInputSuffix: rule => rule.NotionalCurrency ?? '',
      getIsDisabled: rule => !rule.NotionalCurrency,
    },
    {
      field: 'MaxNotionalThreshold',
      type: 'input',
      title: 'Max Notional Threshold',
      guideText: 'Maximum order notional value for this rule to apply.',
      getInputSuffix: rule => rule.NotionalCurrency ?? '',
      getIsDisabled: rule => !rule.NotionalCurrency,
    },
  ];
}

export const RFQ_PROTOCOL_RULES_COLUMNS: ColumnDef<ICustomerOrderExecutionRule>[] = [
  isActiveRuleColumn,
  {
    type: 'prettyText',
    field: 'RFQProtocol',
    title: 'RFQ Protocol',
    initialWidth: 140,
    description: 'Systematic prices automatically; Manual requires dealer intervention.',
  },
  {
    type: 'number',
    field: 'Priority',
    initialWidth: 120,
    sortable: true,
    initialSort: '+',
    description: 'Rules are evaluated in ascending Priority order. The first matching rule wins.',
  },
  {
    type: 'counterparty',
    field: 'Counterparty',
    title: 'Counterparty',
    initialWidth: 170,
    sortable: true,
    description: 'Counterparty this rule applies to',
    params: { showAsteriskOnEmpty: true },
  },
  {
    type: 'security',
    field: 'Symbol',
    initialWidth: 170,
    sortable: true,
    params: { showAsteriskOnEmpty: true },
  },
  {
    type: 'prettyText',
    field: 'ProductType',
    initialWidth: 140,
    initialHide: true,
  },
  {
    type: 'prettyText',
    field: 'Workflow',
    initialWidth: 180,
    initialHide: true,
  },
  { type: 'time', field: 'StartTime', initialWidth: 150 },
  { type: 'time', field: 'EndTime', initialWidth: 150 },
  {
    type: 'size',
    field: 'MinQuantityThreshold',
    initialWidth: 150,
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'BaseCurrency' },
  },
  {
    type: 'size',
    field: 'MaxQuantityThreshold',
    initialWidth: 150,
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'BaseCurrency' },
  },
  {
    type: 'currency',
    field: 'NotionalCurrency',
    title: 'Notional Currency',
    initialWidth: 150,
  },
  {
    type: 'size',
    field: 'MinNotionalThreshold',
    initialWidth: 175,
    initialHide: true,
    params: { currencyField: 'NotionalCurrency' },
  },
  {
    type: 'size',
    field: 'MaxNotionalThreshold',
    initialWidth: 175,
    initialHide: true,
    params: { currencyField: 'NotionalCurrency' },
  },
];

export const RFQ_PROTOCOL_RULES_BULK_EDIT_FIELDS: (keyof ICustomerOrderExecutionRule)[] = [
  'RFQProtocol',
  'Priority',
  'StartTime',
  'EndTime',
  'Workflow',
  'NotionalCurrency',
  'MinNotionalThreshold',
  'MaxNotionalThreshold',
];
